import type { InstanceConfig } from './config.ts';
import { formatUrl, parseUrl, resolveInstanceUrl } from './urls.ts';

/**
 * R2 — image URLs are rewritten **in the parser layer, never in components**, so no component can
 * leak by omission.
 *
 * §2/§8a: the API mixes three URL conventions. `videoThumbnails[].url` is already instance-proxied,
 * but `authorThumbnails[].url` (`yt3.ggpht.com`) and `storyboards[].templateUrl` (`i.ytimg.com`)
 * point straight at Google. Author avatars render on every page and every comment, so hotlinking
 * leaks the viewer's IP to Google continuously — which defeats much of the point of Invidious.
 *
 * Verified against `invidious.example.com` (2026-07-27):
 *
 *   https://yt3.ggpht.com/X               → {instance}/ggpht/X            200 image/jpeg
 *   https://yt3.googleusercontent.com/X   → {instance}/ggpht/X            200 image/jpeg  (same path space)
 *   https://i.ytimg.com/vi/{id}/f.jpg     → {instance}/vi/{id}/f.jpg      200 image/jpeg
 *   https://i.ytimg.com/sb/{id}/{sb}/{f}  → {instance}/sb/i/{id}/{sb}/{f} 200 image/webp
 *
 * The `/sb/` route takes the ytimg host label as its first segment (`i` for `i.ytimg.com`), which
 * is why the storyboard VTT's own cues read `/sb/i/…`. Anything else returns `null`: the caller
 * renders a placeholder rather than a request Google can count.
 */
const GGPHT_HOST_RE = /^(?:[a-z0-9-]+\.)?ggpht\.com$/;
const YT_USERCONTENT_HOST_RE = /^yt[0-9]*\.googleusercontent\.com$/;
const YTIMG_HOST_RE = /^([a-z0-9-]+)\.ytimg\.com$/;

/**
 * §2 — `authorThumbnails[].url` is sometimes **protocol-relative** (`//yt3.ggpht.com/…`), and the
 * shape varies *per record inside one response*: `/api/v1/search?type=channel` returns some
 * channels with `https://` and others with `//`, and a mixed `type=all` page carries both forms in
 * the same array. `parseUrl` rejects `//host/x` deliberately, so without this the avatar simply
 * vanishes — a silent, per-row loss that no error surfaces.
 *
 * Resolved as `https:` rather than against a page scheme: core has no page (R1), and the instance
 * is https by construction (`normalizeInstanceUrl`).
 */
const PROTOCOL_RELATIVE_RE = /^\/\/[^/?#]/;

export function rewriteAssetUrl(config: InstanceConfig, input: string): string | null {
  const trimmed = input.trim();
  const parts = parseUrl(PROTOCOL_RELATIVE_RE.test(trimmed) ? `https:${trimmed}` : trimmed);
  if (!parts) return null;

  // Relative, or already on the instance — the normal transport path handles both.
  if (!parts.absolute) return resolveInstanceUrl(config, input);

  if (GGPHT_HOST_RE.test(parts.host) || YT_USERCONTENT_HOST_RE.test(parts.host)) {
    if (parts.path === '' || parts.path === '/') return null;
    return resolveInstanceUrl(config, formatUrl({ ...parts, absolute: false, scheme: '', host: '', path: `/ggpht${parts.path}` }));
  }

  const ytimg = YTIMG_HOST_RE.exec(parts.host);
  if (ytimg) {
    const authority = ytimg[1] ?? '';
    let path: string | null = null;
    if (parts.path.startsWith('/vi/')) path = parts.path;
    else if (parts.path.startsWith('/sb/')) path = `/sb/${authority}${parts.path.slice('/sb'.length)}`;
    // `/pl_c/{PLID}/studio_square_thumbnail.jpg` — the third shape `playlistThumbnail` comes in, and
    // one a *channel*'s playlists tab uses for a handful of rows (6 of 30 on one channel,
    // the rest `/vi/`). The instance proxies it at the same path, with no host label: verified 200,
    // byte-identical to Google's.
    //
    // **The `sqp=` query is load-bearing, which is the opposite of the `/vi/` case.** Google answers
    // **404** for the bare path, and the parameter is not merely a signature — it encodes the size, and
    // the size differs per playlist: measured 240×240 (13.9 KB) on one and 426×240 on another, despite
    // the identical `studio_square_thumbnail.jpg` filename. 2c measured `sqp` on a `/vi/` frame as
    // byte-for-byte inert and drops it; here it must be carried through untouched, and there is no
    // second filename to retarget to. `retargetVideoFrameUrl` cannot reach this anyway — its pattern
    // only matches `/vi/` — which is the arm working as designed rather than by luck.
    //
    // Without this arm those rows are blank and nothing reports it: the rewrite returns `null`, the
    // model carries `thumbnail: null`, and the card draws its honest empty frame for an image that is
    // in fact available.
    else if (parts.path.startsWith('/pl_c/')) path = parts.path;
    // `null` for any other ytimg path, and `/s_p/` is the one that turns up in real data: a search
    // playlist row for one of YouTube's auto-generated mixes carries
    // `https://i9.ytimg.com/s_p/{RDCLAK5uy_…}/mqdefault.jpg`. Refusing it is correct rather than a gap —
    // this was measured as **404 at Google and 504 through the instance**, so there is nothing
    // to rewrite it to. The card then draws its empty frame, which is the honest rendering of an image
    // that does not exist. Don't add an arm for it.
    if (path === null) return null;
    return resolveInstanceUrl(config, formatUrl({ ...parts, absolute: false, scheme: '', host: '', path }));
  }

  // Instance-origin absolutes resolve; anything else (googlevideo, third-party) is not ours to serve.
  return resolveInstanceUrl(config, input);
}

/**
 * The instance-relative form of an asset URL this instance serves — `/ggpht/AB…`, `/vi/{id}/x.jpg` —
 * or `null` for a URL that is not on this instance at all.
 *
 * It exists for **R14's "store no image URLs"** rule, which is a rule about *origins* rather than
 * about URLs. R14 states it as "thumbnails are derived at render time from `videoId` + the current
 * `instanceUrl`, so the cache survives an instance switch and never pins a dead origin", and for a
 * video that is enough because the path is derivable from the id. A channel avatar's path is **not**
 * derivable from anything (`parse/subscriptions.ts` has the measurements), so a cache that wants to
 * avoid a second 50 KB fetch has to keep it.
 *
 * Keeping the path and dropping the origin honours the rule exactly as stated: what is cached is
 * `/ggpht/WZ_63J…=s48-c-k-c0x00ffffff-no-rj`, which any instance will proxy, and
 * `resolveInstanceUrl(config, path)` at render time makes it the *current* one's. Switching instances
 * leaves every cached avatar working; caching the rewritten absolute URL would leave every one of them
 * pointing at the previous instance, still resolving, and quietly leaking which instance you used to
 * be on to the one you left.
 *
 * The query survives, because for one of these paths it is the address rather than a decoration
 * (`/pl_c/` — see above), and dropping it there would cache a guaranteed 404.
 */
export function instanceAssetPath(config: InstanceConfig, url: string): string | null {
  const parts = parseUrl(url.trim());
  if (parts === null) return null;

  if (parts.absolute) {
    const onInstance = resolveInstanceUrl(config, url);
    // `resolveInstanceUrl` refuses a foreign origin outright (R2), which is exactly the test wanted
    // here — a URL it will not resolve is a URL this instance does not serve.
    if (onInstance === null) return null;
  }

  const path = parts.path === '' ? '/' : parts.path;
  return formatUrl({ ...parts, absolute: false, scheme: '', host: '', path });
}
